/**
 * Vocabulary: readers see this feature as "owner reviews"; code, routes, DealHunt paths and
 * analytics events keep the historical "owner note" name on purpose — do not rename them.
 *
 * The review page's one owner form: closed until an ask on the page (the strip's "Add yours",
 * the carousel's "Add your review") calls `requestOpenOwnerBand`, then the band opens here and
 * scrolls into view. Without the bot check configured the asks keep their product-page link.
 * An author's existing note opens in edit mode, so a second tap never posts a duplicate.
 * Under the sign-in gate (slice D): a create needs a live session, the asks say so.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import OwnerNoteBand, { type OwnerBandLocation } from '../OwnerNoteBand'
import type { GuidePickContext } from 'lib/dealhunt/types'
import { useOwnerTrack } from 'lib/owner-notes/analytics'
import { onOpenOwnerBand } from 'lib/owner-notes/bus'
import { useOwnNote } from 'lib/owner-notes/own-note'
import { SignInGateContext, signInGateFor } from 'lib/owner-notes/signin-gate'
import { bandDraftKey } from 'lib/owner-notes/draft-waiting'

const ReviewOwnerBand: React.VFC<{ context: GuidePickContext; className?: string }> = ({
  context,
  className,
}) => {
  const product = useMemo(
    () => ({ nodeId: context.node_id, name: context.name, href: context.sg_href }),
    [context.node_id, context.name, context.sg_href]
  )
  const trackOwner = useOwnerTrack(product, 'review_page')
  const { me, ownNote, applyResult, applyClaim, clearDevice } = useOwnNote(context.node_id)
  const [origin, setOrigin] = useState<OwnerBandLocation | null>(null)
  const anchor = useRef<HTMLDivElement>(null)

  useEffect(
    () =>
      onOpenOwnerBand(from => {
        setOrigin(from ?? 'section')
        // After the band renders, so the scroll lands on the form, not an empty anchor.
        requestAnimationFrame(() =>
          anchor.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
        )
      }),
    []
  )

  return (
    <div ref={anchor} className={className}>
      {origin && (
        <SignInGateContext.Provider value={signInGateFor('review')}>
          <OwnerNoteBand
            product={product}
            existing={ownNote}
            location={origin}
            trackOwner={trackOwner}
            onClose={() => setOrigin(null)}
            onResult={applyResult}
            onClaimed={applyClaim}
            me={me}
            onDeviceForgotten={clearDevice}
            draftKey={bandDraftKey(context.node_id)}
          />
        </SignInGateContext.Provider>
      )}
    </div>
  )
}

export default ReviewOwnerBand
